import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Legend,
  Title,
  Tooltip,
  TooltipItem
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import axios, { CancelToken } from 'axios';

import { useAuth } from 'hooks/use-auth';
import {
  Status, parseStatuses,
  StatusLog, parseStatusLogs, applyStatusProps
} from 'models';
import { Time } from 'utils';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);


interface Props {
  companyId: number;
  factoryId: number;
  equipmentId: string;
  period?: number;
  interval?: number;
}

export function EquipmentPieChart({
  companyId,
  factoryId,
  equipmentId,
  period = 3600 * 24,
  interval = 60
}: Props) {
  const auth = useAuth();

  const [periodMsec, setPeriodMsec] = useState(Time.secToMsec(period));
  const [intervalMsec, setIntervalMsec] = useState(Time.secToMsec(interval));
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [statusLogs, setStatusLogs] = useState<StatusLog[] | null>(null);
  const [lastDataTimestamp, setLastDataTimestamp] = useState<Date>();
  const [data, setData] = useState<any>({labels: [], datasets: []});

  const refChart = useRef<ChartJS<any>>(null);
  const refStatusLogs = useRef(statusLogs);
  const refLastDataTimestamp = useRef(lastDataTimestamp);

  function dateFrom(): Date {
    return Time.dateFromUnixMsec(Time.unixMsec() - periodMsec);
  }

  function dateTo(): Date {
    return new Date();
  }

  async function fetchStatusLogs(cancelToken: CancelToken) {
    axios.get(`/v0/equipments/${equipmentId}/status-logs`, {
      cancelToken: cancelToken,
      headers: auth.authorizationHeader,
      params: {
        company_id: companyId,
        factory_id: factoryId,
        equipment_id: equipmentId,
        time_from: dateFrom(),
        updated_after: refLastDataTimestamp.current || dateFrom()
      }
    }).then(res => {
      const _dateFrom = dateFrom();
      let slogs: StatusLog[] = refStatusLogs.current || [];
      let new_slogs = parseStatusLogs(res.data)
      new_slogs = new_slogs.map(slog => applyStatusProps(slog, statuses));
      slogs = slogs.concat(new_slogs);
      slogs.sort((a, b) => a.timeFrom.getTime() - b.timeFrom.getTime());
      refStatusLogs.current = slogs.filter((val, idx, self) => (
        self.findIndex(item => item.timeFrom.getTime() === val.timeFrom.getTime()) === idx
      )).filter(item => (
        item.timeTo.getTime() >= _dateFrom.getTime()
      ));
      setStatusLogs(refStatusLogs.current);
      refLastDataTimestamp.current = slogs.reduce((cur: Date, item) => (
        item.updatedAt && (item.updatedAt > cur ? item.updatedAt : cur) || cur
      ), refLastDataTimestamp.current || _dateFrom);
      setLastDataTimestamp(refLastDataTimestamp.current);
    }).catch(err => {
      if (err.code != 'ERR_CANCELED') {
        console.error(err);
      }
    });
  }

  useEffect(() => {
    const cancelTokenSrc = axios.CancelToken.source();
    fetchStatusLogs(cancelTokenSrc.token);
    const intervalId = setInterval(fetchStatusLogs , intervalMsec, cancelTokenSrc.token);
    return () => {
      cancelTokenSrc.cancel(`Cancel API request ID: ${intervalId}`);
      clearInterval(intervalId);
    };
  }, [companyId, factoryId, equipmentId, periodMsec, intervalMsec]);

  useEffect(() => {
    setStatusLogs(statusLogs?.map(slog => applyStatusProps(slog, statuses)) || []);
  }, [statuses]);

  useEffect(() => {
    axios.get(`/v0/factories/${factoryId}/statuses`, {
      headers: auth.authorizationHeader,
      params: {
        equipment_id: equipmentId
      }
    }).then(res => {
      setStatuses(parseStatuses(res.data));
    }).catch(err => {
      console.error(err);
    });
  }, [companyId, factoryId, equipmentId, lastDataTimestamp]);

  useEffect(() => {
    setData(makeData(
      statuses,
      statusLogs || [],
      dateFrom(),
      dateTo()
    ));
    refChart.current?.update('none');
  }, [statusLogs]);

  return (
    <div>
      <Pie
        ref={refChart}
        options={{
          responsive: true,
          animation: false,
          plugins: {
            title: {
              display: true,
              text: `稼働率 (${Time.msecToHour(periodMsec)}h)`,
            },
            legend: {
              display: true,
              position: 'center'
            },
            tooltip: {
              callbacks: {
                label: (item: TooltipItem<any>) => {
                  return `${Time.msecToHour(item.raw as number).toFixed(1)}h`
                }
              }
            }
          }
        }}
        data={data}
        redraw={false}
      />
      {(statusLogs && statusLogs.length < 1) ? (
        `No data`
      ) : (
        <>
        {lastDataTimestamp ? (
          `最終データ日時: ${lastDataTimestamp.toLocaleString()}`
        ) : (
          `Loading ...`
        )}
        </>
      )}
    </div>
  );
}

function makeData(
  statuses: Status[],
  statusLogs: StatusLog[],
  dataTimeFrom: Date,
  dataTimeTo: Date
): any {
  const statusLogsMap = new Map<string | undefined, number>;

  const dataTimeFromUnix = dataTimeFrom.getTime();
  const dataTimeToUnix = dataTimeTo.getTime();
  const periodMsec = dataTimeToUnix - dataTimeFromUnix;

  let otherDuration = periodMsec;
  statusLogs.forEach(slog => {
    const { statusId, timeFrom, timeTo } = slog;
    if (statusId) {
      const timeFromUnix = Math.max(timeFrom.getTime(), dataTimeFromUnix);
      const timeToUnix = Math.min(timeTo.getTime(), dataTimeToUnix);
      const duration = timeToUnix - timeFromUnix;
      statusLogsMap.set(statusId, (statusLogsMap.get(statusId) || 0) + duration);
      otherDuration -= duration;
    }
  });

  if (otherDuration > 0) {
    statusLogsMap.set(undefined, otherDuration);
  }

  const statusLogsProps = Array.from(statusLogsMap.entries()).sort((a, b) => {
    if (a[0] === undefined) return 1;
    if (b[0] === undefined) return -1;
    return b[1] - a[1];
  }).map(([key, val]) => {
    const status = statuses.find(status => status.id === key);
    return {
      label: status ? status.name : '未検出',
      duration: val,
      borderColor: status ? status.colorRgb?.toString() : '#fff',
      backgroundColor: status ? status.colorRgb?.toString() : '#fff'
    };
  });

  return {
    labels: statusLogsProps.map(a => a.label),
    datasets: [{
      label: "",
      data: statusLogsProps.map(a => a.duration),
      borderColor: statusLogsProps.map(a => a.borderColor),
      backgroundColor: statusLogsProps.map(a => a.backgroundColor),
      borderWidth: 1
    }]
  };
}

